import { transformSearch } from "@/api/helper.ts";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import caseActivityService from "../services/activities-service";

export const caseActivitySearchQuery = z.object({
	search: z.string().optional(),
	stage: z.string(),
	id: z.string(),
	case_id: z.string().optional().nullable(),
	per_page: z.number().optional(),
	page: z.number().nullable().optional(),
	sort_by: z.enum(["latest", "oldest"]).optional(),
});

export const caseActivityQueryOptions = (search?: z.infer<typeof caseActivitySearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_activities", search],
		queryFn: () => caseActivityService.lisyByCaseId(search.id, transformSearch(search, ["stage", "search"])),
		...options,
	});
};

export const caseComplianceQueryOptions = (search?: z.infer<typeof caseActivitySearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_activities", search],
		queryFn: () => caseActivityService.lisyByCaseComplianceId(search.id, transformSearch(search, ["stage", "search"])),
		...options,
	});
};

export const useAdminCaseAmmendmentMotionMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_ammendments", activityId],
		mutationFn: caseActivityService.ammendAdminByIdWithMotion,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_ammendments"] });
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};
export const useCaseAmmendmentMotionMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_ammendments", activityId],
		mutationFn: caseActivityService.ammendByIdWithMotion,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_ammendments"] }),
	});
};

export const caseNotificationQueryOptions = (search?: z.infer<typeof caseActivitySearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_notifications", search],
		queryFn: () => caseActivityService.lisyByCaseNotificationId(search.id, transformSearch(search, ["stage", "search"])),
		...options,
	});
};

export const caseDeletionQueryOptions = (search?: z.infer<typeof caseActivitySearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_deletions", search],
		queryFn: () => caseActivityService.lisyByCaseDeletionId(search.id, transformSearch(search, ["stage", "search"])),
		...options,
	});
};

export const caseActivityStenographicNotes = (search?: z.infer<typeof caseActivitySearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_activities", search],
		queryFn: () => caseActivityService.getStenographicNotes(transformSearch(search, ["stage", "search"])),
		...options,
	});
};

export const caseActivitySheriffReports = (search?: z.infer<typeof caseActivitySearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_activities", search],
		queryFn: () => caseActivityService.getSheriffReports(transformSearch(search, ["stage", "search"])),
		...options,
	});
};

export const useCaseActivityMutation = () => {
	return useMutation({
		mutationKey: ["case_activities"],
		mutationFn: caseActivityService.createById,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityScheduleHearingMutation = () => {
	return useMutation({
		mutationKey: ["case_activities"],
		mutationFn: caseActivityService.scheduleHearing,
		meta: {
			successMessage: "Hearing saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityHearingMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.createByHearing,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
		},
	});
};

export const useCaseActivityMoveHearingMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateStage,
		meta: {
			successMessage: "Activity moved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityShareMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.shareById,
		meta: {
			successMessage: "Activity shared successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseStageUpdateMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateCaseStage,
		meta: {
			successMessage: "Stage changed successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_records"] }),
	});
};

export const useCaseActivityUpdateMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateById,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};
export const useCaseActivityEditMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateActivityById,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityUpdateSubActivityMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateSubActivityById,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseConvertDocsMutation = () => {
	return useMutation({
		mutationKey: ["case_activities"],
		mutationFn: caseActivityService.convertDocstoPdf,
		meta: {
			successMessage: "Docs converted successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityUpdateProofMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateProofById,
		meta: {
			successMessage: "Proof of Payment saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityApprovalMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.approvalById,
		meta: {
			successMessage: "Activity status updated successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityTitleMutation = () => {
	return useMutation({
		mutationKey: ["case_activities"],
		mutationFn: caseActivityService.updateTitleById,
		meta: {
			successMessage: "Title saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityDeleteReasonMutation = () => {
	return useMutation({
		mutationKey: ["case_activities"],
		mutationFn: caseActivityService.deleteWithReason,
		meta: {
			successMessage: "Case activity deleted successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityNotifyMutation = () => {
	return useMutation({
		mutationKey: ["case_activities"],
		mutationFn: caseActivityService.notifyById,
		meta: {
			successMessage: "Case parties has been notified successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityStatusMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateStatusById,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useActivityViewRequestMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.grantViewRequest,
		meta: {
			successMessage: "Activity view request grant successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivityMotionMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.createByIdWithMotion,
		meta: {
			successMessage: "Activity saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivitySheriffFileMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateSheriffReportFile,
		meta: {
			successMessage: "Sheriff Report saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const useCaseActivitySheriffMutation = (activityId?: string) => {
	return useMutation({
		mutationKey: ["case_activities", activityId],
		mutationFn: caseActivityService.updateSheriffReport,
		meta: {
			successMessage: "Sheriff Report saved successfully",
		},
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["case_activities"] }),
	});
};

export const caseActivityModelQueryOptions = (caseId: string, caseRecordId: string) => {
	return queryOptions({
		queryKey: ["case_activity", caseRecordId, caseId],
		queryFn: () => caseActivityService.findByCaseId(caseId, caseRecordId),
	});
};

export const useCaseActivityDeleteMutation = () => {
	return useMutation({
		mutationKey: ["case_activities", "delete"],
		mutationFn: caseActivityService.deleteById,
		meta: {
			successMessage: "Case Activity archived successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["case_activities"],
			});
		},
	});
};
